import React, { BaseSyntheticEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IonCol, IonRow, IonIcon, IonLabel } from '@ionic/react'
import { stopwatchOutline } from 'ionicons/icons'

import '../page/Times.page.css'
import {
  getCurrentDate,
  getCurrentTime,
  transformDate,
  transformTime,
  addKeyboardSpacer,
  removeKeyboardSpacer,
  isPlatformWeb,
} from '../../../utilities'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import Validation from '../../../components/validation/Validation.component'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { appendSeconds, truncateSecondsIfNecessary } from '../../../utilities/dateTime.util'
import { IOptions, IPNNVOption } from '../../../common/common.types'
import PNNVIcon from '../../../components/pnnv/PNNVIcon.component'
import { resetPNNVState } from '../../../components/pnnv/store/pnnv.actions'

export interface ITimesRowProps {
  elementId?: string
  header: string
  time: string
  date: string
  timeAction: string
  dateAction: string
  currentDateTimeAction: string
  disabled?: boolean | null
  subHeader?: string | null
  cardinalityKey?: string
  pnnvOptions?: IOptions
  pnnvValue?: IPNNVOption | null | undefined
  pnnvAction?: (...args: any[]) => any
  pnnvLabel?: string
  pnnvListHeader?: string
  useBothNormalAndPertinent?: boolean
  isStopWatchDisabled?: boolean | null
  overridePicklistWithNumberPad?: boolean
}

const TimesRow: React.FC<ITimesRowProps> = (props: ITimesRowProps) => {
  const {
    elementId,
    header,
    time,
    date,
    timeAction,
    dateAction,
    disabled,
    subHeader,
    cardinalityKey,
    pnnvAction,
    pnnvLabel,
    pnnvListHeader,
    pnnvOptions,
    pnnvValue,
    useBothNormalAndPertinent = false,
    isStopWatchDisabled = false,
    overridePicklistWithNumberPad = true,
  } = props
  const dispatch = useDispatch()
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const shouldShowPNNVPWAControl =
    isPlatformWeb() && typeof pnnvAction !== 'undefined' && pnnvOptions && pnnvOptions.length > 0 && pnnvValue === null

  const setTime = (numbers: string[], oldTime?: string) => {
    const oldTimeParts = oldTime?.split('') || []
    if (oldTimeParts.includes(':') && !numbers.includes(':')) {
      numbers.pop()
    }
    const newTime = transformTime(numbers.join(''), displaySeconds)
    dispatch({ type: timeAction, time: newTime })
  }

  const setDate = (numbers: string[], oldDate?: string) => {
    const oldDateParts = oldDate?.split('') || []
    const oldFwdSlashCount = oldDateParts.filter((o: string) => o === '/').length
    const newFwdSlashCount = numbers.filter((n: string) => n === '/').length
    if (newFwdSlashCount < oldFwdSlashCount) {
      numbers.pop()
    }
    const newDate = transformDate(numbers.join(''))
    dispatch({ type: dateAction, date: newDate })
  }

  const setCurrentDateAndTime = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    removeKeyboardSpacer()
    const date = getCurrentDate()
    let time = getCurrentTime()
    if (!displaySeconds) {
      // If seconds are not displayed, replace the times' seconds with :00
      time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
    }

    dispatch({ type: timeAction, time: time })
    dispatch({ type: dateAction, date: date })
  }

  const inputOnFocus = (e: CustomEvent) => {
    e.preventDefault()
    removeKeyboardSpacer()
    if (elementId) {
      if (!isPlatformWeb()) addKeyboardSpacer(elementId)
    }
  }

  const onBlur = () => {
    const newTime = appendSeconds(time, displaySeconds)
    dispatch({ type: timeAction, time: newTime })
  }

  const handlePNNVIconClick = (e: CustomEvent | React.MouseEvent<HTMLIonButtonElement>) => {
    if (!pnnvOptions?.length) {
      dispatch(resetPNNVState())
    }
  }

  const renderPNNVIcon = () =>
    shouldShowPNNVPWAControl && (
      <PNNVIcon
        pnnvOptions={pnnvOptions}
        pnnvLabel={pnnvLabel}
        pnnvValue={pnnvValue}
        pnnvListHeader={pnnvListHeader}
        pnnvAction={pnnvAction}
        onButtonClick={handlePNNVIconClick}
        className='pnnv-icon-pwa-numberpad'
        dataTestId={header + '-pnnvIcon'}
      />
    )

  return (
    <div id={elementId} className='times-row-container'>
      <IonRow className='ion-padding-horizontal'>
        <IonCol className='ion-hide-md-up times-row-header'>
          <div className={`d-flex align-items-center ${shouldShowPNNVPWAControl ? 'gap-x-3' : ''}`}>
            <IonLabel>{header}</IonLabel>
            <IonLabel className='times-row-header-info-text'>{!!disabled && !!subHeader && `${subHeader}`}</IonLabel>
            {!disabled && renderPNNVIcon()}
          </div>
        </IonCol>
      </IonRow>
      <IonRow data-testid='TimesRow' className='ion-padding-horizontal'>
        <IonCol className='ion-hide-md-down'>
          <div className={`d-flex align-items-center ${shouldShowPNNVPWAControl ? 'gap-x-2' : ''}`}>
            <IonLabel>{header}</IonLabel>
            <IonLabel className='times-row-header-info-text mt-05'>
              {!!disabled && !!subHeader && `${subHeader}`}
            </IonLabel>
            {!disabled && renderPNNVIcon()}
          </div>
        </IonCol>
        <IonCol className='picklist' data-testid='Column'>
          <NumberPad
            dataTestId={`${header}Time`}
            className='times-button'
            placeholder={displaySeconds ? 'HH:MM:SS' : 'HH:MM'}
            values={truncateSecondsIfNecessary(time || '', displaySeconds).split('')}
            onNumPadClick={setTime}
            onFocus={inputOnFocus}
            onBlur={onBlur}
            disabled={!!disabled}
            pnnvAction={pnnvAction}
            pnnvLabel={pnnvLabel}
            pnnvListHeader={pnnvListHeader}
            pnnvOptions={pnnvOptions}
            pnnvValue={pnnvValue}
            useBothNormalAndPertinent={useBothNormalAndPertinent}
            overridePicklistWithNumberPad={overridePicklistWithNumberPad}
          />
        </IonCol>

        <IonIcon
          tabIndex={0}
          data-testid={`${header}StopWatch`}
          icon={stopwatchOutline}
          className='times-stopwatch'
          size='large'
          color='primary-alt'
          onKeyPress={isStopWatchDisabled || pnnvValue ? undefined : setCurrentDateAndTime}
          onClick={isStopWatchDisabled || pnnvValue ? undefined : setCurrentDateAndTime}
          style={isStopWatchDisabled || pnnvValue ? { opacity: '0.4' } : undefined}
        />

        <IonCol className='picklist times-row-end-button' data-testid='Column'>
          <NumberPad
            dataTestId={`${header}Date`}
            className='times-button'
            placeholder='MM/DD/YY'
            values={(date || '').split('')}
            onNumPadClick={setDate}
            onFocus={inputOnFocus}
            disabled={!!disabled}
            pnnvAction={pnnvAction}
            pnnvLabel={pnnvLabel}
            pnnvListHeader={pnnvListHeader}
            pnnvOptions={pnnvOptions}
            pnnvValue={pnnvValue}
            useBothNormalAndPertinent={useBothNormalAndPertinent}
            overridePicklistWithNumberPad={overridePicklistWithNumberPad}
          />
        </IonCol>
        <IonCol className='ion-hide-md-down' />
      </IonRow>
      <IonRow>
        <Validation id={timeAction} cardinalityKey={cardinalityKey} />
        <Validation id={dateAction} cardinalityKey={cardinalityKey} />
      </IonRow>
    </div>
  )
}

export default TimesRow
